import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import JeaButton from "@/components/jea/JeaButton";
import JeaInput from "@/components/jea/JeaInput";
import { useTags } from "@/context/TagsContext";
import { TAG_PALETTE } from "@/lib/tags";
import { cn } from "@/lib/utils";

/**
 * "New Tag" (also used for Edit Tag) modal.
 * `existing` (optional) → edit mode. Otherwise creates a new tag.
 */
const NewTagModal = ({ open, defaultName = "", existing, onClose, onCreated, onUpdated }) => {
  const { createTag, updateTag, palette } = useTags();
  const colors = palette?.length ? palette : TAG_PALETTE;
  const [name, setName] = useState("");
  const [color, setColor] = useState(colors[0]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setName(existing.name || "");
      setColor(existing.color || colors[0]);
    } else {
      setName(defaultName || "");
      setColor(colors[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing, defaultName]);

  const submit = async () => {
    const trimmed = (name || "").trim();
    if (!trimmed) {
      toast.error("Tag name is required.");
      return;
    }
    setBusy(true);
    try {
      if (existing) {
        const updated = await updateTag(existing.id, { name: trimmed, color });
        onUpdated?.(updated);
      } else {
        const created = await createTag({ name: trimmed, color });
        toast.success(`Created tag '${trimmed}'.`);
        onCreated?.(created);
      }
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't save tag.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-[12px] z-[95]"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={busy ? undefined : onClose}
          />
          <motion.div
            className="fixed inset-0 z-[96] flex items-center justify-center px-4 py-6"
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            data-testid="new-tag-modal"
          >
            <div
              className="w-full max-w-[420px] bg-bg-secondary border border-border-default rounded-[12px] p-6 shadow-elevated"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="jea-caption">{existing ? "Edit Tag" : "New Tag"}</div>
                  <h3 className="text-white font-bold text-[18px]">
                    {existing ? "Update tag" : "Create a new tag"}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={busy}
                  className="p-1 text-text-secondary hover:text-white disabled:opacity-40"
                  data-testid="new-tag-close"
                >
                  <X size={14} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="jea-caption mb-1 block">Name</label>
                  <JeaInput
                    autoFocus
                    maxLength={32}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. High Priority"
                    data-testid="new-tag-name"
                  />
                </div>
                <div>
                  <label className="jea-caption mb-1 block">Color</label>
                  <div className="flex flex-wrap gap-2" data-testid="new-tag-color-swatches">
                    {colors.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setColor(c)}
                        className={cn(
                          "w-8 h-8 rounded-full border-2 transition-transform",
                          color === c ? "border-white scale-110" : "border-transparent hover:scale-105"
                        )}
                        style={{ background: c }}
                        aria-label={`Color ${c}`}
                        data-testid={`new-tag-color-${c.replace('#', '')}`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={busy}
                  className="h-10 px-4 rounded-[8px] border border-border-default text-text-secondary hover:text-white hover:bg-bg-tertiary text-[13px] font-semibold transition-colors disabled:opacity-40"
                  data-testid="new-tag-cancel"
                >
                  Cancel
                </button>
                <JeaButton size="sm" onClick={submit} loading={busy} data-testid="new-tag-save">
                  {existing ? "Save Changes" : "Create Tag"}
                </JeaButton>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NewTagModal;
